/* eslint-disable react/require-default-props */
import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react';
import normalizeLocationId from '../utils/normalizeLocationId';
import type { Location } from '../types';

export type LocationTreeContextType = {
  selectedLocations: Location[];
  onSelectedChange: (location: Location) => void;
  focusedNodeId: string | null;
  setFocusedNodeId: (id: string | null) => void;
};

const LocationTreeContext = createContext<LocationTreeContextType | undefined>(
  undefined
);

export const useLocationTreeContext = () => {
  const context = useContext(LocationTreeContext);
  if (!context) {
    throw new Error(
      'useLocationTreeContext must be used within a LocationTreeProvider'
    );
  }
  return context;
};

export const LocationTreeProvider = ({
  children,
  selectedLocations = [],
  onSelectedChange = () => {},
  focusedNodeId,
  setFocusedNodeId,
}: {
  children: React.ReactNode;
  selectedLocations?: Location[];
  onSelectedChange?: (location: Location) => void;
  focusedNodeId: string | null;
  setFocusedNodeId: (id: string | null) => void;
}) => {
  const [contextSelectedLocations, setContextSelectedLocations] = useState<
    Location[]
  >(() => selectedLocations || []);

  useEffect(() => {
    setContextSelectedLocations(selectedLocations || []);
  }, [selectedLocations]);

  const handleSelectedChange = useCallback(
    (location: Location) => {
      setContextSelectedLocations((prevSelectedLocations) => {
        let newSelectedLocations: Location[] = [...prevSelectedLocations];

        if (
          newSelectedLocations.some(
            (loc) =>
              normalizeLocationId(loc.id) === normalizeLocationId(location.id)
          )
        ) {
          newSelectedLocations = newSelectedLocations.filter(
            (loc) =>
              normalizeLocationId(loc.id) !== normalizeLocationId(location.id)
          );
        } else {
          newSelectedLocations.push(location);
        }

        return newSelectedLocations;
      });

      onSelectedChange?.(location);
    },
    [onSelectedChange]
  );

  const contextValue: LocationTreeContextType = useMemo(
    () => ({
      selectedLocations: contextSelectedLocations,
      onSelectedChange: handleSelectedChange,
      focusedNodeId,
      setFocusedNodeId,
    }),
    [
      contextSelectedLocations,
      handleSelectedChange,
      focusedNodeId,
      setFocusedNodeId,
    ]
  );

  return (
    <LocationTreeContext.Provider value={contextValue}>
      {children}
    </LocationTreeContext.Provider>
  );
};

export const useLocationTreeProvider = (): LocationTreeContextType => {
  const context = useContext(LocationTreeContext);

  if (!context) {
    throw new Error(
      'useLocationTreeProvider must be used within a LocationTreeProvider'
    );
  }

  return context;
};
